import { Button, Chip } from '@components/common';
import {
  MicrophoneIcon,
  VideoCameraIcon,
  VideoCameraSlashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { cn } from '@utils/cn';
import { useTranslation } from 'next-i18next';
import { useCallback, useState } from 'react';
import Webcam from 'react-webcam';

interface WebcamPreviewProps {
  hasGivenMediaPermissions: boolean;
  hasRejectedPermission: boolean;
  timeLeft: number | null;
  webcamRef: React.RefObject<Webcam | null>;
  countdown?: number | null;
}

/**
 * @description Component to display successful completion of an AI interview.
 *
 * @param {WebcamPreviewProps} props - The component props.
 * - **hasGivenMediaPermissions**: boolean (required) - if media permissions have been granted.
 * - **hasRejectedPermission**: boolean (required) - if the user has rejected media permissions.
 * - **timeLeft**: number (required) - representing the remaining recording time in seconds.
 * - **webcamRef**: React.RefObject<Webcam | null> - ref for the Webcam component.
 * - **countdown**: number (optional) - how long until recording starts.
 */
export const WebcamPreview = (props: WebcamPreviewProps) => {
  const {
    hasGivenMediaPermissions,
    hasRejectedPermission,
    timeLeft,
    webcamRef,
    countdown,
  } = props;
  const { t } = useTranslation('common');

  const [isAudioOnly, setIsAudioOnly] = useState<boolean>(false);

  // Format time for display
  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (time % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  // Toggle between video and audio-only mode
  const toggleAudioOnly = useCallback(() => {
    const webcam = webcamRef.current;

    if (webcam && webcam.stream) {
      const videoTracks = webcam.stream.getVideoTracks();
      videoTracks.forEach((track) => {
        track.enabled = isAudioOnly;
      });
      setIsAudioOnly(!isAudioOnly);
    }
  }, [isAudioOnly, webcamRef]);

  return (
    <div className="relative h-80 overflow-hidden rounded-md md:h-102">
      {hasGivenMediaPermissions ? (
        <>
          {/* Audio-only toggle */}
          {countdown && (
            <div className="absolute right-4 top-4 z-2">
              <Button
                styling="solid"
                color={isAudioOnly ? 'primary' : 'light'}
                rounding="full"
                size="xs"
                onClick={toggleAudioOnly}
                className="relative z-2 opacity-80"
              >
                {isAudioOnly ? (
                  <VideoCameraIcon className="size-4" />
                ) : (
                  <VideoCameraSlashIcon className="size-4" />
                )}
              </Button>
            </div>
          )}

          {/* Remaining Time overlay */}
          {timeLeft !== null && (
            <div className="absolute left-4 top-4 z-1">
              <Chip color="light" className="w-20 border-transparent font-medium opacity-80">
                {formatTime(timeLeft)}
              </Chip>
            </div>
          )}

          {/* Countdown overlay */}
          {countdown && (
            <div className="absolute inset-0 z-1 flex size-full items-center justify-center bg-black/50">
              <div className="flex size-24 items-center justify-center rounded-full bg-error text-4xl font-bold text-white">
                {countdown}
              </div>
            </div>
          )}

          {/* Webcam component */}
          <Webcam
            audio
            muted
            ref={webcamRef}
            videoConstraints={{
              facingMode: 'user',
            }}
            className="size-full object-cover"
          />
        </>
      ) : (
        <div className="z-1 flex size-full flex-col items-center justify-center bg-surface-100 px-4 py-10">
          <p
            className={cn(
              'mb-5 text-center text-lg font-bold text-error',
              hasRejectedPermission ? 'text-error' : 'text-ink',
            )}
          >
            {hasRejectedPermission
              ? t('video.cv.error.camera_permission')
              : t('video.cv.microphone.camera')}
          </p>
          <div className="flex gap-4">
            <div className="relative flex size-14 items-center justify-center rounded-full bg-surface">
              <VideoCameraIcon className="size-6 text-ink" />
              <div className="absolute -right-1 bottom-1 size-4 rounded-full bg-error">
                <XMarkIcon className="size-4 text-white" />
              </div>
            </div>
            <div className="relative flex size-14 items-center justify-center rounded-full bg-surface">
              <MicrophoneIcon className="size-6 text-ink" />
              <div className="absolute -right-1 bottom-1 size-4 rounded-full bg-error">
                <XMarkIcon className="size-4 text-white" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
